import autoTable from "jspdf-autotable";
import {
  addBoldText,
  autoTextColor,
  buildCalculatorNotes,
  buildScheduleCompanyHeader,
  getCompanyNameHeaderLayout,
  hasCrossYABasisHeader,
} from "./page";
import { applyChineseFontToCellIfNeeded } from "./fontUtils";
import { getSubscriptionStatus } from "./subscriptionStatus";

export function generateCurrencyLabel(currency) {
  return currency === "SGD" ? "$" : currency;
}

/**
 * Enhances the reference column in table body: values over maxLen chars are
 * replaced with "Ref N" and the full text is collected for a details/remarks section.
 * Use the returned body for the table and append formatted referenceDetails to notes.
 *
 * @param {Array<Array>} body - Table body rows
 * @param {number} refColumnIndex - 0-based index of the reference column
 * @param {number} maxLen - Max chars to show in cell (default 15). Over this → "Ref N".
 * @returns {{ body: Array<Array>, referenceDetails: Array<{ ref: string, fullText: string }> }}
 */
export function enhanceReferenceColumn(body, refColumnIndex, maxLen = 15) {
  const referenceDetails = [];
  let refCounter = 0;
  const getText = (ref) => {
    if (ref == null) return "";
    if (typeof ref === "string") return ref;
    if (typeof ref === "object" && ref.content != null)
      return String(ref.content);
    return String(ref);
  };
  const newBody = (body || []).map((row) => {
    if (!row || row.length <= refColumnIndex) return row;
    const ref = row[refColumnIndex];
    const str = getText(ref);
    if (str.length > maxLen) {
      refCounter++;
      referenceDetails.push({ ref: `Ref ${refCounter}`, fullText: str });
      const newRow = [...row];
      newRow[refColumnIndex] = autoTextColor(`Ref ${refCounter}`);
      return newRow;
    }
    return row;
  });
  return { body: newBody, referenceDetails };
}

/**
 * Formats reference details for appending to notes/remarks (e.g. after the table).
 *
 * @param {Array<{ ref: string, fullText: string }>} referenceDetails - From enhanceReferenceColumn
 * @returns {string} Formatted text, or "" if none.
 */
export function formatReferenceDetails(referenceDetails) {
  if (!referenceDetails || referenceDetails.length === 0) return "";
  return referenceDetails
    .map(({ ref, fullText }) => `${ref}: ${fullText}`)
    .join("\n");
}

export function applyHeaderStyles(data, index, columnIndexes) {
  return (
    data.section === "head" &&
    data.row.index === index &&
    columnIndexes.includes(data.column.index)
  );
}

export function insertTableOfContents(pdf, company = null, schedule = null) {
  const doc = pdf.doc;

  // Ensure metadata exists
  if (
    !pdf._meta ||
    !Array.isArray(pdf._meta.registeredPages) ||
    pdf._meta.registeredPages.length === 0
  ) {
    console.warn("No registered pages found for Table of Contents.");
    return;
  }

  const tocMap = new Map();

  for (const section of pdf._meta.registeredPages) {
    const { ref, pages } = section;

    if (!tocMap.has(ref)) {
      tocMap.set(ref, {
        ref,
        pages: [...pages],
      });
    } else {
      tocMap.get(ref).pages.push(...pages);
    }
  }

  const tocEntries = Array.from(tocMap.values()).map((section) => {
    const firstPage = Math.min(...section.pages);
    const lastPage = Math.max(...section.pages);

    return {
      title: section.ref,
      pageText:
        firstPage === lastPage ? `${firstPage}` : `${firstPage}-${lastPage}`,
      firstPage,
    };
  });

  // Sort TOC by first page (just in case)
  tocEntries.sort((a, b) => a.firstPage - b.firstPage);

  // Build head/body for autotable
  const head = [["Asset Code", "Asset Name", "Page Number"]];
  const body = tocEntries.map((entry) => [
    entry.title.split(" | ")[0] || "Untitled Section",
    entry.title.split(" | ")[1] || "",
    entry.pageText,
  ]);

  // Save total pages before insertion
  const pageCount = doc.internal.getNumberOfPages();

  // Estimate TOC pages needed
  // Calculate available height per page (accounting for header, title, margins)
  const rowsPerPage = 27;
  const totalRows = 1 + body.length;
  const estimatedTocPages = Math.max(1, Math.ceil(totalRows / rowsPerPage));

  // Insert TOC pages at the beginning
  // First, insert all needed pages
  for (let i = 0; i < estimatedTocPages; i++) {
    if (i === 0) {
      // First page: insert at position 1
      doc.addPage("a4", "portrait");
      doc.deletePage(doc.internal.getNumberOfPages());
      doc.insertPage(1);
    } else {
      // Additional pages: insert after the previous TOC page
      doc.insertPage(i + 1);
    }
  }

  // Set to first TOC page
  doc.setPage(1);

  // Render using buildAutoTable so headers/footers are consistent
  buildAutoTable({
    pdf,
    head,
    body,
    company,
    schedule,
    printHeader: true,
    printFooter: true,
    addPlainTitle: true,
    tableTitle: "Table of Contents",
    checkHeaderCondition: true,
    skipWatermark: true, // Skip watermark for table of contents
    columnStyles: {
      0: { halign: "left", cellWidth: 45 },
      1: { halign: "left" },
      2: { halign: "right" },
    },
    didParseCell: function (data) {
      if (data.row.index === 0 && [0].includes(data.column.index)) {
        data.cell.styles.halign = "left";
      }
      if (data.row.index === 0 && [1].includes(data.column.index)) {
        data.cell.styles.halign = "left";
      }
      if (data.row.index === 0 && [2].includes(data.column.index)) {
        data.cell.styles.halign = "right";
      }
    },
  });

  // Get actual number of pages used by TOC
  const actualTocPages = doc.internal.getNumberOfPages() - pageCount;

  // Restore cursor to first page after TOC
  doc.setPage(actualTocPages + 1);
}

export const constructFooter = (pdf, data, printFooter) => {
  // Initialize custom metadata container if it doesn't exist
  if (!pdf._meta) {
    pdf._meta = {
      footers: new Set(),
      curRef: null, // For table of contents or section tracking
      registeredPages: [],
    };
  }

  const currentPage = pdf.doc.internal.getCurrentPageInfo().pageNumber;

  // Print page number only if printFooter is true
  if (printFooter) {
    // Avoid duplicate footers
    if (!pdf._meta.footers.has(currentPage)) {
      // Optional: use pdf._meta.curRef in your footer if desired
      let refLabel = pdf._meta.curRef ? ` (${pdf._meta.curRef})` : "";
      let str = `Page ${currentPage}${refLabel}`;

      pdf.doc.setFontSize(8);
      pdf.doc.text(
        str,
        data.settings.margin.left,
        pdf.doc.internal.pageSize.height - 10
      );

      pdf._meta.footers.add(currentPage);
    }
  }

  // Add "Trial" text on the right side if subscription status is "trialing"
  // This should always show regardless of printFooter setting
  const subscriptionStatus = pdf.subscriptionStatus || getSubscriptionStatus();
  if (
    subscriptionStatus === "trialing" &&
    process.env.REACT_APP_RENDER_ENV === true
  ) {
    const pageWidth = pdf.doc.internal.pageSize.width;
    const pageHeight = pdf.doc.internal.pageSize.height;
    pdf.doc.setFontSize(8);
    const trialText = "TRIAL VERSION";
    const textWidth = pdf.doc.getTextWidth(trialText);
    pdf.doc.text(
      trialText,
      pageWidth - data.settings.margin.right - textWidth,
      pageHeight - 10
    );
  }

  if (printFooter) {
    const curRef = pdf._meta.curRef;

    if (curRef) {
      const registered = pdf._meta.registeredPages || [];

      // 🔒 Find existing entry for this ref
      let entry = registered.find((e) => e.ref === curRef);

      if (entry) {
        // 🔒 Avoid duplicate page registration
        if (!entry.pages.includes(currentPage)) {
          entry.pages.push(currentPage);
        }
      } else {
        // 🆕 First time seeing this ref
        registered.push({
          ref: curRef,
          pages: [currentPage],
        });
      }

      pdf._meta.registeredPages = registered;
    }
  }
};

export const buildAutoTable = ({
  pdf,
  head,
  body,
  columnStyles = {},
  didParseCell,
  didDrawCell,
  schedule,
  company,
  printHeader = true,
  printFooter = false,
  customStartY = 60,
  checkHeaderCondition = true,
  customHeadStyles,
  addTableTitle,
  addPlainTitle,
  tableTitle,
  tableTitleFontSize = 10,
  customFs,
  lineSettings,
  skipWatermark = false, // Flag to skip watermark (e.g., for table of contents)
  ...props
}) => {
  // If Excel mode, use Excel generator
  if (pdf.isExcel) {
    const excel = pdf.getExcel();
    if (excel) {
      // Pad body rows to match header column count for Excel
      const headerColCount = head && head[0] ? head[0].length : 0;
      const paddedBody = body
        ? body.map((row) => {
            if (!row || row.length === 0) return [];
            const padded = [...row];
            // Calculate actual column count considering colSpan
            let actualCols = 0;
            row.forEach((cell) => {
              if (cell && typeof cell === "object" && cell.colSpan) {
                actualCols += cell.colSpan;
              } else {
                actualCols += 1;
              }
            });
            // Pad to match header if needed
            while (actualCols < headerColCount) {
              padded.push("");
              actualCols += 1;
            }
            return padded;
          })
        : [];

      excel.buildAutoTable({
        head,
        body: paddedBody,
        columnStyles,
        didParseCell,
        schedule,
        company,
        lineSettings,
      });
    }
    return;
  }

  const hasCrossYABasis = hasCrossYABasisHeader(company);
  const extraHeaderHeight = printHeader
    ? getCompanyNameHeaderLayout(pdf.doc, company, pdf.leftX).extraHeight
    : 0;
  const minStartY = (hasCrossYABasis ? 65 : 60) + extraHeaderHeight;
  const headerMarginTop =
    pdf.initY + (hasCrossYABasis ? 45 : 40) + extraHeaderHeight;
  const firstPageHeaderStartThreshold =
    (hasCrossYABasis ? 75 : 70) + extraHeaderHeight;
  if (customStartY < minStartY) {
    customStartY = minStartY;
  }
  // PDF mode - original implementation
  if (addTableTitle || addPlainTitle) {
    if (pdf.startY > 160) {
      pdf.doc.addPage();
      customStartY = minStartY;
    }
    if (addTableTitle) {
      pdf.doc.setTextColor(255, 0, 0);
    }
    addBoldText(
      pdf.doc,
      tableTitle,
      pdf.leftX,
      customStartY - 5,
      tableTitleFontSize || 10
    );
    if (addTableTitle) pdf.doc.setTextColor(0, 0, 0);
  }

  autoTable(pdf.doc, {
    startY: customStartY,
    head,
    body,
    theme: "plain",
    styles: { fontSize: customFs || pdf.fs },
    headStyles: customHeadStyles || {
      fontStyle: "bold",
      halign: "center",
      textColor: [0, 0, 0],
    },
    margin: {
      top: headerMarginTop,
      bottom: 20.61,
      left: pdf.leftX - 1,
    },
    columnStyles,
    didParseCell: (data) => {
      applyChineseFontToCellIfNeeded(pdf, data);
      if (typeof didParseCell === "function") {
        didParseCell(data);
      }
    },
    didDrawCell,
    ...props,
    ...(extraHeaderHeight > 0 && props.margin?.top !== undefined
      ? {
          margin: {
            ...props.margin,
            top: Math.max(props.margin.top, headerMarginTop),
          },
        }
      : {}),
    didDrawPage: (data) => {
      if (printHeader) {
        let headerY = pdf.initY;

        let buildHeader = true;

        let endY = 250;
        if (pdf.isLandscape) {
          endY = 175;
        }
        if (checkHeaderCondition)
          buildHeader =
            (data.pageCount === 1 &&
              (customStartY < firstPageHeaderStartThreshold ||
                customStartY > endY)) ||
            data.pageCount > 1;

        if (buildHeader)
          buildScheduleCompanyHeader(
            company,
            schedule,
            pdf.doc,
            pdf.leftX,
            pdf.rightX,
            headerY,
            pdf.fs
          );
      }

      // Add watermark if subscription is not active and not skipped (e.g., for TOC)
      if (!skipWatermark) {
        pdf.addWatermark();
      }

      constructFooter(pdf, data, printFooter);
      pdf.startY = pdf.doc.lastAutoTable.finalY + 10;
    },
  });

  pdf.startY = pdf.doc.lastAutoTable.finalY + 10;
};

export const buildAutoNote = (
  pdf,
  forView,
  company,
  schedule,
  notes,
  reviewNotes,
  dataRows,
  printFooter = false,
  notesHeader = "Notes/Comments",
  options = {}
) => {
  // If Excel mode, use Excel generator
  if (pdf.isExcel) {
    const excel = pdf.getExcel();
    if (excel) {
      excel.buildNotes(notes, reviewNotes, forView, dataRows, company);
    }
    return;
  }

  // PDF mode - original implementation
  let noteContent = [];
  let noteY = pdf.doc.lastAutoTable.finalY + 10;
  if (notes) {
    noteContent.push(...buildNotesRows(notes, 1, notesHeader));
  }

  reviewNotes = buildCalculatorNotes(reviewNotes, dataRows);
  if (reviewNotes && forView) {
    if (notes) noteContent.push([]);
    noteContent.push(...buildReviewNotesRows(reviewNotes, 1));
  }

  const noteYLimit = pdf.isLandscape ? 175 : 250;
  const hasCrossYABasis = hasCrossYABasisHeader(company);
  const extraHeaderHeight = getCompanyNameHeaderLayout(
    pdf.doc,
    company,
    pdf.leftX
  ).extraHeight;
  const notesMinStartY = (hasCrossYABasis ? 76 : 60) + extraHeaderHeight;
  const notesMarginTop = (hasCrossYABasis ? 76 : 65) + extraHeaderHeight;
  let shouldDrawHeaderOnFirstNotesPage = false;
  if (noteY > noteYLimit && noteContent.length > 0) {
    pdf.doc.addPage("a4", pdf.isLandscape ? "landscape" : "portrait");
    noteY = notesMinStartY;
    shouldDrawHeaderOnFirstNotesPage = true;
  }

  if (noteContent.length > 0)
    autoTable(pdf.doc, {
      startY: noteY,
      head: [],
      body: noteContent,
      theme: "plain",
      styles: {
        fontSize: pdf.fs,
        ...(options.styles || {}),
      },
      didParseCell: (data) => {
        applyChineseFontToCellIfNeeded(pdf, data);
      },
      margin: { top: notesMarginTop },
      headStyles: {
        fontStyle: "bold",
        halign: "center",
        textColor: [0, 0, 0],
      },
      bodyStyles: {
        cellPadding: { top: 3, right: 3, bottom: 1, left: 3 },
        ...(options.bodyStyles || {}),
      },
      didDrawPage: (data) => {
        let headerY = pdf.initY;
        if (
          (data.pageCount === 1 && shouldDrawHeaderOnFirstNotesPage) ||
          (data.pageCount === 1 && noteY < notesMarginTop) ||
          data.pageCount > 1
        )
          buildScheduleCompanyHeader(
            company,
            schedule,
            pdf.doc,
            pdf.leftX,
            pdf.rightX,
            headerY,
            pdf.fs
          );

        // Add watermark if subscription is not active
        pdf.addWatermark();

        constructFooter(pdf, data, printFooter);
      },
    });
};

export const buildNotesRows = (
  notes,
  colSpan,
  notesHeader = "Notes/Comments"
) => {
  if (!notes) return [];
  return [
    [
      {
        content: notesHeader,
        colSpan,
        styles: {
          fontStyle: "bold",
          halign: "left",
          textColor: [0, 0, 0],
          lineWidth: { bottom: 0.1 },
          lineColor: [0, 0, 0],
          cellPadding: 1,
        },
      },
    ],
    [
      {
        content: `${notes}`,
        colSpan,
        styles: { halign: "left", textColor: [0, 0, 0] },
      },
    ],
  ];
};

export const buildReviewNotesRows = (reviewNotes, colSpan) => {
  if (!reviewNotes) return [];
  return [
    [
      {
        content: "Internal Review Notes",
        colSpan,
        styles: {
          fontStyle: "bold",
          halign: "left",
          textColor: [255, 0, 0],
          lineWidth: { bottom: 0.1 },
          lineColor: [255, 0, 0],
          cellPadding: 1,
        },
      },
    ],
    [
      {
        content: `${reviewNotes}`,
        colSpan,
        styles: { halign: "left", textColor: [255, 0, 0] },
      },
    ],
  ];
};
